/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { sendDataLayer } from "jiffy-analytics";
import { generateRandomString } from "common/utils/helpers/random";
import productPage from "../product-details-quantity-form/product-page";

export const PDP_PAGE_VISIT = "PDPPageVisit";
export const ADD_TO_CART = "AddToCart";

const SELECTORS = {
  DTF_CONTAINER: "#vue-product-details-dtf",
  PRODUCT_CONTAINER: "#product-page-container"
};

const EVENT_ID_PREFIX = {
  VIEW_CONTENT: "VC",
  PAGE_VIEW: "PV"
};

const getElements = {
  get $dtfContainer() {
    return document.querySelector(SELECTORS.DTF_CONTAINER);
  },

  get $productContainer() {
    return document.querySelector(SELECTORS.PRODUCT_CONTAINER);
  },

  get $formEventId() {
    return document.querySelector(productPage.selectors.formEventId);
  }
};

/**
 * Get app configuration from DTF container dataset
 * @returns {Object} Configuration object with product, variant, and FB data
 */
const getAppConfig = () => {
  const $container = getElements.$dtfContainer;

  // @ts-expect-error TS(2339): Property 'dataset' does not exist on type 'Element... Remove this comment to see the full error message
  if (!$container?.dataset?.appConfig) {
    console.error("DTF container or app config not found");
    return undefined;
  }

  try {
    // @ts-expect-error TS(2339): Property 'dataset' does not exist on type 'Element... Remove this comment to see the full error message
    const config = JSON.parse($container.dataset.appConfig);
    return {
      product: config.product,
      variant: config.variant,
      fbData: JSON.parse(config.fb_data || "{}")
    };
  } catch (error) {
    console.error("Failed to parse app config:", error);
    return undefined;
  }
};

/**
 * Generate product display name
 */
const getProductName = (product: any) => `${product.brand} ${product.catalog_number}`;

/**
 * Build Facebook content object
 */
// @ts-expect-error TS(7006): Parameter 'product' implicitly has an 'any' type.
const buildFbContent = (product, fbData, contentId) => ({
  content_ids: [contentId],
  content_name: getProductName(product),
  content_category: product.adwords_category,
  content_type: "product",
  user_data: fbData
});

/**
 * Safely parse price value
 * @param {*} price - Price value to parse
 * @returns {number} Parsed price or 0 if invalid
 */
const parsePrice = (price: any) => Number(price) || 0;

/**
 * Build item viewed object for analytics
 */
const buildItemsViewed = (product: any, variant: any) => [
  {
    name: getProductName(product),
    id: variant.sku,
    brand: product.brand,
    price: product.retail_price,
    google_business_vertical: "retail"
  }
];

/**
 * Build ecommerce data object
 */
// @ts-expect-error TS(7006): Parameter 'product' implicitly has an 'any' type.
export const buildEcommerceData = (product, variant) => ({
  currency: variant.currency,
  value: parsePrice(product.retail_price),

  items: [
    {
      item_id: variant.sku,
      item_name: getProductName(product),
      item_brand: product.brand,
      item_category: product.adwords_category,
      price: product.retail_price
    }
  ]
});

/**
 * Generate event ID with prefix and timestamp
 */
const generateEventId = (prefix: any, userId: any) => `${prefix}-${userId}-${Date.now()}`;

/**
 * Track product detail page visit
 */
export const trackPDPPageVisit = () => {
  const config = getAppConfig();
  if (!config) return;

  const { product, variant, fbData } = config;
  const userIdentifier = fbData.external_id?.[0] || generateRandomString();

  const dataLayerPayload = {
    event: PDP_PAGE_VISIT,
    items: [{ product_id: variant.sku }],
    msAdProductIds: [variant.sku],
    viewItemValue: product.retail_price,
    pageViewEventId: generateEventId(EVENT_ID_PREFIX.PAGE_VIEW, userIdentifier),
    viewContentEventId: generateEventId(EVENT_ID_PREFIX.VIEW_CONTENT, userIdentifier),
    fbContent: buildFbContent(product, fbData, variant.sku),
    itemsViewed: buildItemsViewed(product, variant),
    ecommerce: buildEcommerceData(product, variant)
  };

  sendDataLayer(dataLayerPayload);
};

/**
 * Track add to cart event
 * @param {number} quantity - Quantity of items added
 * @param {number} price - Total price of items
 */
export const trackAddToCart = (quantity: any, price: any) => {
  const config = getAppConfig();
  if (!config) return;

  const { product, variant, fbData } = config;

  // @ts-expect-error TS(2339): Property 'value' does not exist on type 'Element'.
  const addToCartEventId = getElements.$formEventId?.value;
  // @ts-expect-error TS(2339): Property 'dataset' does not exist on type 'Element... Remove this comment to see the full error message
  const email = getElements.$productContainer?.dataset?.orderEmail;

  const itemsAdded = {
    id: variant.sku,
    name: getProductName(product),
    brand: product.brand,
    price: product.retail_price,
    currency: variant.currency,
    sku: variant.sku,
    quantity
  };

  const dataLayerPayload = {
    event: ADD_TO_CART,
    itemsAdded,
    email,
    addToCartEventId,
    addToCartValue: price,
    fbContent: buildFbContent(product, fbData, variant.sku),
    ecommerce: buildEcommerceData(product, variant)
  };

  sendDataLayer(dataLayerPayload);
};
